import React from 'react';
import { ArrowRight, CheckCircle2, Globe, Quote, Target, Eye } from 'lucide-react';
import { companyInfo } from '../../data/companyInfo';

interface AboutSectionProps {
  onLearnMore: () => void;
}

export const AboutSection: React.FC<AboutSectionProps> = ({ onLearnMore }) => {
  return (
    <section className="bg-[#071426] py-16 sm:py-20 border-b border-[#1b2f4a] relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          
          {/* Left Visual: Modern Engineering Team Collaboration */}
          <div className="lg:col-span-6 relative">
            <div className="relative rounded-2xl overflow-hidden border border-[#1b2f4a] bg-[#050A13] shadow-2xl group">
              <img
                src="/assets/about-engineering-team.jpg"
                alt="EBON Media Solutions - Senior Engineering and Digital Strategy Team"
                className="w-full h-[360px] sm:h-[420px] object-cover group-hover:scale-[1.02] transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#071426] via-[#071426]/30 to-transparent pointer-events-none" />
              
              {/* Engineering Culture Overlay Badge */}
              <div className="absolute bottom-4 left-4 right-4 bg-[#050A13]/90 backdrop-blur-md p-4 rounded-xl border border-[#1b2f4a] shadow-xl">
                <div className="flex items-center justify-between text-xs mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full bg-[#159DE8] animate-pulse" />
                    <span className="font-bold text-white uppercase tracking-wider text-[11px]">Engineering & Product Excellence</span>
                  </div>
                  <div className="flex items-center gap-1 text-[11px] text-[#159DE8] font-medium">
                    <Globe size={12} className="text-[#159DE8]" />
                    <span>Global Delivery Pods</span>
                  </div>
                </div>
                
                <p className="text-xs text-[#D5DFEC] leading-relaxed border-t border-[#1b2f4a]/60 pt-2">
                  Cross-functional agile teams of full-stack engineers, cloud architects, and UX strategists delivering mission-critical web and mobile solutions.
                </p>
              </div>
            </div>
          </div>

          {/* Right Content */}
          <div className="lg:col-span-6 space-y-6 text-left">
            <div>
              <span className="text-xs font-bold uppercase tracking-wider text-[#FF851B] block mb-2">
                ABOUT EBON MEDIA SOLUTIONS
              </span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white leading-tight font-display">
                Technology Built Around <br className="hidden sm:inline" />
                Your <span className="text-[#159DE8]">Business</span>
              </h2>
            </div>

            <p className="text-[#D5DFEC] text-base leading-relaxed">
              We are a forward-thinking IT solutions company, delivering innovative software, AI-powered solutions, and digital marketing strategies to help businesses grow in the digital era.
            </p>

            <p className="text-sm text-[#8FA0B8] leading-relaxed">
              Our multidisciplinary engineering teams combine cutting-edge software architecture, human-centered UI/UX design, and data-driven marketing to solve complex operational challenges and build sustainable competitive advantage for companies across the US, UK, and Canada.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
              <div className="flex items-center gap-2 text-xs text-[#D5DFEC]">
                <CheckCircle2 size={16} className="text-[#159DE8] shrink-0" />
                <span>Zero vendor lock-in & full IP ownership</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-[#D5DFEC]">
                <CheckCircle2 size={16} className="text-[#159DE8] shrink-0" />
                <span>Transparent milestone-driven sprints</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-[#D5DFEC]">
                <CheckCircle2 size={16} className="text-[#159DE8] shrink-0" />
                <span>Modern cloud-native tech stacks</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-[#D5DFEC]">
                <CheckCircle2 size={16} className="text-[#159DE8] shrink-0" />
                <span>Dedicated ongoing SLA support</span>
              </div>
            </div>

            <div className="pt-4 flex items-center gap-4">
              <button
                onClick={onLearnMore}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-[#FF851B] text-[#FF851B] hover:bg-[#FF851B] hover:text-white font-semibold text-sm transition-all duration-200 shadow-sm hover:shadow-glow-orange"
              >
                <span>About EBON</span>
                <ArrowRight size={16} />
              </button>
            </div>

          </div>

        </div>

        {/* Mission & Vision Mini Strip */}
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div className="p-5 rounded-2xl bg-[#050A13] border border-[#159DE8]/30 flex items-start gap-4 hover:border-[#159DE8]/60 transition-colors group">
            <div className="w-10 h-10 rounded-xl bg-[#159DE8]/15 border border-[#159DE8]/30 flex items-center justify-center shrink-0 group-hover:bg-[#159DE8]/25 transition-colors">
              <Target size={20} className="text-[#159DE8]" />
            </div>
            <div>
              <span className="text-[10px] font-mono uppercase tracking-widest text-[#159DE8] block mb-0.5">Our Mission</span>
              <h4 className="text-sm font-bold text-white mb-1">Technology That Creates Real Progress</h4>
              <p className="text-xs text-[#8FA0B8] leading-relaxed">Empowering businesses with intelligent software, robust apps, and data-driven strategies that turn ambition into commercial reality.</p>
            </div>
          </div>
          <div className="p-5 rounded-2xl bg-[#050A13] border border-[#FF851B]/30 flex items-start gap-4 hover:border-[#FF851B]/60 transition-colors group">
            <div className="w-10 h-10 rounded-xl bg-[#FF851B]/15 border border-[#FF851B]/30 flex items-center justify-center shrink-0 group-hover:bg-[#FF851B]/25 transition-colors">
              <Eye size={20} className="text-[#FF851B]" />
            </div>
            <div>
              <span className="text-[10px] font-mono uppercase tracking-widest text-[#FF851B] block mb-0.5">Our Vision</span>
              <h4 className="text-sm font-bold text-white mb-1">A Globally Trusted Technology Partner</h4>
              <p className="text-xs text-[#8FA0B8] leading-relaxed">To be recognized worldwide for technical integrity, human-centric design, and software ecosystems that scale and deliver value for decades.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
